import { Text, VStack } from "@chakra-ui/react"
import { useNavigate } from "react-router-dom"
import Dialog from "@hydrologiq/shared/chakra/Dialog"
import { SegmentedControl } from "@hydrologiq/shared/chakra/SegmentedControl"
import { useColorMode, type ColourModePreference } from "@hydrologiq/shared/chakra/ColorMode"
import { useActiveOrganization } from "../auth/AuthProvider"

/** The tenant feature (admin Tenant Features) that offers Dark and System; Light is always offered. */
export const DARK_MODE_FEATURE = "dark_mode"
export const DARK_MODE_NOT_ENABLED = "Feature not enabled, contact administrator to enable"

const APPEARANCE_OPTIONS: { value: ColourModePreference; label: string }[] = [
  { value: "light", label: "Light" },
  { value: "dark", label: "Dark" },
  { value: "system", label: "System" },
]

/**
 * UserSettingsDialog - per-user preferences, opened from the user menu at /settings/preferences.
 * Used as a nested route within Dashboard to prevent section reloads.
 *
 * Appearance sets the Colour Mode Preference. It is saved in this browser only
 * (localStorage), and "System" follows the operating system's light or dark setting.
 * Dark and System are offered only where the tenant has the dark_mode feature; otherwise
 * they're disabled, with a tooltip saying why.
 */
export const UserSettingsDialog = () => {
  const navigate = useNavigate()
  const { preference, setColorMode } = useColorMode()
  const { features } = useActiveOrganization()
  const darkModeEnabled = features.some((f) => f.name === DARK_MODE_FEATURE && f.enabled)
  const items = APPEARANCE_OPTIONS.map((option) =>
    option.value === "light" || darkModeEnabled
      ? option
      : { ...option, disabled: true, tooltip: DARK_MODE_NOT_ENABLED },
  )

  return (
    <Dialog open={true} onClose={() => navigate("/")} title="User Settings">
      <VStack align="stretch" gap={2} width="100%">
        <Text id="appearance-label" fontWeight="semibold">
          Appearance
        </Text>
        <SegmentedControl
          aria-labelledby="appearance-label"
          variant="hydrologiq_segmented"
          items={items}
          value={preference ?? "light"}
          onValueChange={(details) => details.value && setColorMode(details.value)}
        />
        <Text fontSize="sm" color="primary_light">
          System follows your device setting. Saved in this browser.
        </Text>
      </VStack>
    </Dialog>
  )
}
